import React from 'react';

import AdminLayout from 'Pages/Admin/AdminLayout';
import { IndexTable } from 'Components/Utilities';
import { ModalTrigger } from 'Components/Modal';
import Confirm from 'Components/Partials/Modals/Confirm';
import { PrimaryButton } from 'Components/Button';

import WaitlistApi from 'Services/Api/Admin/Waitlist';
import { Toast } from 'Services';

import ChangeStatusModal from './ChangeStatusModal';

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const formatDate = (value) => {
    if (!value) return '-';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        return value.date_time_utc ?? value.iso_string ?? value.date_time_timezone ?? JSON.stringify(value);
    }
    return String(value);
};

const OFFER_BADGE = {
    pending:  { label: 'Pending',  cls: 'bg-yellow-100 text-yellow-800' },
    accepted: { label: 'Accepted', cls: 'bg-green-100  text-green-800'  },
    declined: { label: 'Declined', cls: 'bg-red-100    text-red-800'    },
    expired:  { label: 'Expired',  cls: 'bg-gray-100   text-gray-600'   },
};

const STATUS_LABELS = {
    confirmed_in_person: 'Confirmed In-Person',
    external_waitlist:   'External Waitlist',
    internal_waitlist:   'Internal Waitlist',
    virtual:             'Virtual',
    cancelled:           'Cancelled',
};

const VIEW_OPTIONS = [
    { value: 'stats',            label: 'Stats'                             },
    { value: 'current_waitlist', label: 'Current Waitlist'                  },
    { value: 'all_attendees',    label: 'All In-Person / Virtual Attendees' },
    { value: 'audit_log',        label: 'Audit Log'                         },
];

/* ─── column definitions ──────────────────────────────────────────────────── */

const buildRowActions = (item) => [
    {
        type: 'modalTrigger',
        component: ChangeStatusModal,
        props: { user: item },
        text: 'Change Status',
    },
];

const WAITLIST_COLUMNS = [
    {
        label: 'Name',
        value: (r) => `${r.first_name} ${r.last_name}`,
        type: 'callback',
        orderable: 'last_name',
    },
    {
        label: 'Email',
        value: 'email',
        type: 'string',
        orderable: 'email',
    },
    {
        label: 'Status',
        value: (r) => STATUS_LABELS[r.attendance_status] ?? r.attendance_status ?? '-',
        type: 'callback',
        orderable: 'attendance_status',
    },
    {
        label: 'Joined Waitlist',
        value: (r) => formatDate(r.waitlist_joined_at),
        type: 'callback',
        orderable: 'waitlist_joined_at',
    },
    {
        label: 'Priority',
        value: (r) => r.waitlist_priority ?? 0,
        type: 'callback',
        orderable: 'waitlist_priority',
    },
    {
        label: 'Offer Sent',
        value: (r) => r.latest_waitlist_offer
            ? formatDate(r.latest_waitlist_offer.offered_at)
            : <span className="text-gray-400">—</span>,
        type: 'callback',
        orderable: null,
    },
    {
        label: 'Offer Status',
        value: (r) => {
            const offer = r.latest_waitlist_offer;
            if (!offer) return <span className="text-gray-400 text-xs">—</span>;
            const badge = OFFER_BADGE[offer.status] ?? { label: offer.status, cls: 'bg-gray-100 text-gray-600' };
            return (
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
                    {badge.label}
                </span>
            );
        },
        type: 'callback',
        orderable: 'offer_status',
    },
];

const ATTENDEES_COLUMNS = [
    {
        label: 'Name',
        value: (r) => `${r.first_name} ${r.last_name}`,
        type: 'callback',
        orderable: 'last_name',
    },
    {
        label: 'Email',
        value: 'email',
        type: 'string',
        orderable: 'email',
    },
    {
        label: 'Status',
        value: (r) => STATUS_LABELS[r.attendance_status] ?? r.attendance_status ?? '-',
        type: 'callback',
        orderable: 'attendance_status',
    },
    {
        label: 'Registered',
        value: (r) => formatDate(r.registered_at),
        type: 'callback',
        orderable: 'registered_at',
    },
    {
        label: 'Offer Status',
        value: (r) => {
            const offer = r.latest_waitlist_offer;
            if (!offer) {
                if (r.attendance_status === 'confirmed_in_person') {
                    return (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Confirmed
                        </span>
                    );
                }
                return <span className="text-gray-400 text-xs">—</span>;
            }
            const badge = OFFER_BADGE[offer.status] ?? { label: offer.status, cls: 'bg-gray-100 text-gray-600' };
            return (
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
                    {badge.label}
                </span>
            );
        },
        type: 'callback',
        orderable: 'offer_status',
    },
];

const AUDIT_COLUMNS = [
    {
        label: 'When',
        value: (r) => formatDate(r.created_at),
        type: 'callback',
        orderable: 'created_at',
    },
    {
        label: 'User',
        value: (r) => r.user?.email ?? '-',
        type: 'callback',
        orderable: null,
    },
    {
        label: 'Actor',
        value: (r) => r.actor?.email ?? 'System',
        type: 'callback',
        orderable: null,
    },
    {
        label: 'Event',
        value: 'event_type',
        type: 'string',
        orderable: 'event_type',
    },
    {
        label: 'From',
        value: (r) => STATUS_LABELS[r.from_status] ?? r.from_status ?? '-',
        type: 'callback',
        orderable: null,
    },
    {
        label: 'To',
        value: (r) => STATUS_LABELS[r.to_status] ?? r.to_status ?? '-',
        type: 'callback',
        orderable: null,
    },
];

/* ─── page component ──────────────────────────────────────────────────────── */

export default class WaitlistIndex extends React.Component {
    state = {
        statsWorking: true,
        capacity: null,
        counts: null,
        activeView: 'stats',
    };

    componentDidMount() {
        this.loadStats();
    }

    loadStats = async () => {
        this.setState({ statsWorking: true });
        const res = await WaitlistApi.index();
        this.setState({
            statsWorking: false,
            capacity: res?.data?.capacity ?? null,
            counts:   res?.data?.counts   ?? null,
        });
    };

    triggerReallocation = async () => {
        const res = await WaitlistApi.reallocate();
        if (res?.success) {
            Toast.success(res?.data?.message ?? 'Waitlist reallocation completed.');
        } else {
            Toast.error(res?.message ?? 'Unable to run waitlist reallocation right now.');
        }
        this.loadStats();
    };

    render() {
        const { statsWorking, capacity, counts, activeView } = this.state;

        return (
            <AdminLayout title="Waitlist & Attendance">
                <div className="space-y-6">

                    {/* Toolbar */}
                    <div className="flex flex-wrap gap-3 items-center px-3">
                        <PrimaryButton text="Refresh Stats" onClick={this.loadStats} working={statsWorking} />

                        <ModalTrigger
                            component={Confirm}
                            props={{
                                title: 'Run Reallocation',
                                message: 'This will re-run the waitlist allocation. Continue?',
                                onConfirm: this.triggerReallocation,
                            }}
                        >
                            <PrimaryButton text="Run Reallocation" onClick={() => false} />
                        </ModalTrigger>

                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">View:</span>
                            <select
                                className="border border-gray-300 rounded px-2 py-1 text-sm"
                                value={activeView}
                                onChange={(e) => this.setState({ activeView: e.target.value })}
                            >
                                {VIEW_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Stats */}
                    {activeView === 'stats' && (
                        <div className="bg-white p-4 rounded border border-gray-200 space-y-4">
                            <div>
                                <h3 className="text-lg font-semibold mb-3">Capacity</h3>
                                <div className="grid md:grid-cols-5 gap-3 text-sm">
                                    <div>In-person capacity: <strong>{capacity?.in_person_capacity ?? '-'}</strong></div>
                                    <div>Confirmed in-person: <strong>{capacity?.confirmed_in_person ?? '-'}</strong></div>
                                    <div>Outstanding offers: <strong>{capacity?.outstanding_offers ?? '-'}</strong></div>
                                    <div>Remaining seats: <strong>{capacity?.remaining ?? '-'}</strong></div>
                                    <div>Full: <strong className={capacity?.is_full ? 'text-red-600' : 'text-green-600'}>{capacity?.is_full ? 'Yes' : 'No'}</strong></div>
                                    <div>Executive held places: <strong>{capacity?.executives ?? '-'}</strong></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Remaining seats sets aside one seat for every outstanding offer, so the room cannot be over-filled if they are all accepted.
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Executive held places were set aside before the room was sized, so they are shown here but never counted against capacity.
                                </p>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold mb-3">Attendance Snapshot</h3>
                                <div className="grid md:grid-cols-4 gap-3 text-sm">
                                    <div>In-person attendees (excl. exec): <strong>{counts?.confirmed_in_person ?? '-'}</strong></div>
                                    <div>Executive held places: <strong>{counts?.executives ?? '-'}</strong></div>
                                    <div>Virtual attendees: <strong>{counts?.virtual ?? '-'}</strong></div>
                                    <div>External waitlist: <strong>{counts?.external_waitlist ?? '-'}</strong></div>
                                    <div>Internal waitlist: <strong>{counts?.internal_waitlist ?? '-'}</strong></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Current Waitlist */}
                    {activeView === 'current_waitlist' && (
                        <IndexTable
                            columns={WAITLIST_COLUMNS}
                            defaultOrder={{ order_by: 'waitlist_joined_at', order: 'asc' }}
                            displaySearchBar={true}
                            loadDataCallback={(data) => WaitlistApi.waitlistEntries(data)}
                            rowActions={buildRowActions}
                            eventApi={WaitlistApi}
                            itemName="waitlist entries"
                        />
                    )}

                    {/* All Attendees */}
                    {activeView === 'all_attendees' && (
                        <IndexTable
                            columns={ATTENDEES_COLUMNS}
                            defaultOrder={{ order_by: 'last_name', order: 'asc' }}
                            displaySearchBar={true}
                            loadDataCallback={(data) => WaitlistApi.attendeesList(data)}
                            rowActions={buildRowActions}
                            eventApi={WaitlistApi}
                            itemName="attendees"
                        />
                    )}

                    {/* Audit Log */}
                    {activeView === 'audit_log' && (
                        <IndexTable
                            columns={AUDIT_COLUMNS}
                            defaultOrder={{ order_by: 'created_at', order: 'desc' }}
                            displaySearchBar={false}
                            loadDataCallback={(data) => WaitlistApi.auditLogs(data)}
                            eventApi={WaitlistApi}
                            itemName="audit records"
                        />
                    )}

                </div>
            </AdminLayout>
        );
    }
}

